import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../../../../firebase';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import questions from './triviaquestions/questions';

const TriviaRelampago = ({ onClose }) => {
  const [gameState,          setGameState]          = useState('menu');
  const [category,           setCategory]           = useState('general');
  const [currentQuestion,    setCurrentQuestion]    = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score,              setScore]              = useState(0);
  const [timeLeft,           setTimeLeft]           = useState(null);
  const [gameTime,           setGameTime]           = useState(null);
  const [combo,              setCombo]              = useState(0);
  const [multiplier,         setMultiplier]         = useState(1);
  const [user,               setUser]               = useState(null);
  const [highScoresLibre,    setHighScoresLibre]    = useState([]);
  const [highScoresNormal,   setHighScoresNormal]   = useState([]);
  const [highScoresUltra,    setHighScoresUltra]    = useState([]);
  const [personalBestLibre,  setPersonalBestLibre]  = useState(0);
  const [personalBestNormal, setPersonalBestNormal] = useState(0);
  const [personalBestUltra,  setPersonalBestUltra]  = useState(0);
  const [mode,               setMode]               = useState('normal');
  const [lastCorrect,        setLastCorrect]        = useState(null); // true | false | null
  const audioCtxRef = useRef(null);

  const categories = [
    { id: 'general',  name: 'Cultura General' },
    { id: 'movies',   name: 'Cine' },
    { id: 'games',    name: 'Videojuegos' },
    { id: 'history',  name: 'Historia' },
    { id: 'sports',   name: 'Deportes' },
  ];

  // ─── Firebase ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) loadAllData();
      else {
        setHighScoresLibre([]); setHighScoresNormal([]); setHighScoresUltra([]);
        setPersonalBestLibre(0); setPersonalBestNormal(0); setPersonalBestUltra(0);
      }
    });
    return () => unsub();
  }, []);

  const loadAllScores = async () => {
    try {
      const snap = await getDocs(collection(db, 'GameZone/TriviaRelampago/scores'));
      return snap.docs.map((d) => d.data());
    } catch { return []; }
  };

  const loadAllData = async () => {
    const all = await loadAllScores();
    setHighScoresLibre(all.filter((s) => s.mode === 'libre').sort((a, b) => b.score - a.score).slice(0, 5));
    setHighScoresNormal(all.filter((s) => s.mode === 'normal').sort((a, b) => b.score - a.score).slice(0, 5));
    setHighScoresUltra(all.filter((s) => s.mode === 'ultrarrelampago').sort((a, b) => b.score - a.score).slice(0, 5));
    if (user) {
      const byUser = (m) => all.filter((s) => s.userId === user.uid && s.mode === m).reduce((mx, c) => Math.max(mx, c.score || 0), 0);
      setPersonalBestLibre(byUser('libre')); setPersonalBestNormal(byUser('normal')); setPersonalBestUltra(byUser('ultrarrelampago'));
    }
  };

  const saveScoreToFirestore = async (s) => {
    if (!user || s <= 0) return;
    try {
      await addDoc(collection(db, 'GameZone/TriviaRelampago/scores'), {
        userId: user.uid,
        name: user.displayName || user.email || 'Anónimo',
        score: s, mode, date: new Date().toISOString(),
      });
      await loadAllData();
    } catch {}
  };

  // ─── Audio ────────────────────────────────────────────────────────────────
  const initAudio = () => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { audioCtxRef.current = null; }
    }
  };

  const playSfx = (type) => {
    if (!audioCtxRef.current) return;
    try {
      const o = audioCtxRef.current.createOscillator(), g = audioCtxRef.current.createGain();
      o.connect(g); g.connect(audioCtxRef.current.destination);
      if (type === 'correct')      { o.frequency.value = 600; g.gain.value = 0.10; o.start(); o.stop(audioCtxRef.current.currentTime + 0.20); }
      else if (type === 'wrong')   { o.frequency.value = 200; g.gain.value = 0.10; o.start(); o.stop(audioCtxRef.current.currentTime + 0.30); }
      else if (type === 'combo')   { o.frequency.value = 800; g.gain.value = 0.15; o.start(); o.stop(audioCtxRef.current.currentTime + 0.25); }
    } catch {}
  };

  // ─── Game logic ───────────────────────────────────────────────────────────
  const getRandomQuestion = () => {
    const pool = questions[category];
    const q    = pool[Math.floor(Math.random() * pool.length)];
    return { question: q.q, options: q.opts, answer: q.opts[q.a], difficulty: q.d };
  };

  const getMaxTime = () => (mode === 'normal' ? 10 : mode === 'ultrarrelampago' ? 3 : null);

  const startGame = () => {
    initAudio();
    setGameState('playing'); setScore(0); setCombo(0); setMultiplier(1);
    setCurrentQuestionIndex(1); setCurrentQuestion(getRandomQuestion());
    setLastCorrect(null);
    const mt = getMaxTime();
    setTimeLeft(mt);
    setGameTime(mode === 'libre' ? null : mode === 'normal' ? 180 : 60);
  };

  const handleAnswer = (selected) => {
    if (!currentQuestion) return;
    const correct = selected === currentQuestion.answer;
    const mt      = getMaxTime();
    const ansTime = mt !== null ? mt - timeLeft : 0;
    setLastCorrect(correct);

    let pts = 0;
    if (correct) {
      pts = mode === 'libre' ? 5 : 10;
      if (mode !== 'libre') { if (ansTime < 2) pts += 5; else if (ansTime < 5) pts += 2; }
      setCombo((prev) => {
        const nc = prev + 1;
        if (nc >= 3) { setMultiplier(2); playSfx('combo'); }
        return nc;
      });
      playSfx('correct');
    } else {
      pts = -5; setCombo(0); setMultiplier(1); playSfx('wrong');
    }
    setScore((prev) => prev + pts * multiplier);
    nextQuestion();
  };

  const nextQuestion = () => {
    setCurrentQuestion(getRandomQuestion());
    setCurrentQuestionIndex((p) => p + 1);
    setTimeLeft(getMaxTime());
    setTimeout(() => setLastCorrect(null), 300);
  };

  const endGame = async () => {
    await saveScoreToFirestore(score);
    setGameState('gameOver');
  };

  // ─── Timers ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (gameState !== 'playing') return;
    let gameTimer, questionTimer;

    if (gameTime !== null) {
      gameTimer = setInterval(() => {
        setGameTime((p) => { if (p <= 1) { clearInterval(gameTimer); endGame(); return 0; } return p - 1; });
      }, 1000);
    }
    if (timeLeft !== null) {
      questionTimer = setInterval(() => {
        setTimeLeft((p) => {
          if (p === null) return null;
          if (p <= 0) { setCombo(0); setMultiplier(1); nextQuestion(); return getMaxTime(); }
          return p - 0.1;
        });
      }, 100);
    }
    return () => { if (gameTimer) clearInterval(gameTimer); if (questionTimer) clearInterval(questionTimer); };
  }, [gameState, currentQuestionIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render: Menu ─────────────────────────────────────────────────────────
  const renderMenu = () => (
    <div className="w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl lg:max-w-4xl">
      <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-4 sm:p-6 rounded-2xl shadow-2xl text-white">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-2 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          Trivia Relámpago ⚡🧠
        </h2>
        <p className="mb-4 text-center text-sm text-white/65">¡Responde rápido, acumula puntos y compite en el ranking!</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Left: config */}
          <div>
            <h3 className="text-sm font-semibold text-white/70 uppercase tracking-widest mb-2">Categoría</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all active:scale-95 ${category === cat.id ? 'bg-blue-600 text-white font-semibold' : 'bg-gray-700/70 text-white/70 hover:bg-gray-600'}`}
                  onClick={() => setCategory(cat.id)}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <h3 className="text-sm font-semibold text-white/70 uppercase tracking-widest mb-2">Modo de juego</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { id: 'libre',           label: 'Libre (∞)' },
                { id: 'normal',          label: 'Normal (3 min)' },
                { id: 'ultrarrelampago', label: 'Ultrarrelámpago (1 min)' },
              ].map((m) => (
                <button
                  key={m.id}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all active:scale-95 ${mode === m.id ? 'bg-purple-600 text-white font-semibold' : 'bg-gray-700/70 text-white/70 hover:bg-gray-600'}`}
                  onClick={() => setMode(m.id)}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {user && (
              <div className="bg-white/5 rounded-xl p-3">
                <h3 className="text-sm font-semibold text-white/70 uppercase tracking-widest mb-2">Tus mejores</h3>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div><p className="text-white/45 mb-0.5">Libre</p><p className="font-bold text-purple-300">{personalBestLibre}</p></div>
                  <div><p className="text-white/45 mb-0.5">Normal</p><p className="font-bold text-blue-300">{personalBestNormal}</p></div>
                  <div><p className="text-white/45 mb-0.5">Ultra</p><p className="font-bold text-yellow-300">{personalBestUltra}</p></div>
                </div>
              </div>
            )}
          </div>

          {/* Right: rankings */}
          <div className="space-y-3 max-h-[260px] overflow-y-auto pr-1">
            {[
              { label: 'Libre', scores: highScoresLibre },
              { label: 'Normal', scores: highScoresNormal },
              { label: 'Ultrarrelámpago', scores: highScoresUltra },
            ].map(({ label, scores }) => (
              <div key={label}>
                <h3 className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1">Top 5 — {label}</h3>
                {user ? (
                  scores.length ? scores.map((hs, i) => (
                    <div key={i} className="py-1 border-b border-gray-700/60 flex justify-between text-xs">
                      <span>{i + 1}. <strong>{hs.name}</strong></span>
                      <span className="text-white/60">{hs.score}</span>
                    </div>
                  )) : <p className="text-xs text-white/35">Sin puntuaciones aún</p>
                ) : <p className="text-xs text-white/35">Inicia sesión para ver el ranking</p>}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 shadow-lg"
            onClick={startGame}
          >
            ▶ Jugar ahora
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Render: Playing ──────────────────────────────────────────────────────
  const renderGame = () => {
    if (!currentQuestion) return null;
    const mt = getMaxTime();

    return (
      <div className="flex flex-col gap-3 w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl text-white px-1">
        {/* Score / timer bar */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm sm:text-base font-bold">
            Pts: {score}
            {multiplier > 1 && (
              <span className="ml-1.5 text-yellow-400 text-xs font-bold">×{multiplier} COMBO</span>
            )}
          </div>
          {gameTime !== null && (
            <div className={`text-sm sm:text-base font-bold tabular-nums ${gameTime <= 10 ? 'text-red-400' : 'text-white'}`}>
              ⏱ {gameTime}s
            </div>
          )}
          <button
            className="bg-red-600/80 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold active:scale-95 transition-all"
            onClick={endGame}
          >
            Terminar
          </button>
        </div>

        {/* Question card */}
        <div className="bg-gray-800/90 rounded-2xl shadow-xl overflow-hidden">
          {/* Per-question timer bar */}
          {timeLeft !== null && (
            <div className="h-1.5 bg-gray-700">
              <div
                className="h-full rounded-full transition-all duration-100 ease-linear"
                style={{
                  width: `${(timeLeft / mt) * 100}%`,
                  background: timeLeft / mt > 0.4 ? '#3b82f6' : timeLeft / mt > 0.2 ? '#f59e0b' : '#ef4444',
                }}
              />
            </div>
          )}

          <div className="p-4 sm:p-5">
            {/* Question # */}
            <p className="text-white/35 text-xs font-medium mb-2">Pregunta #{currentQuestionIndex}</p>

            {/* Question text */}
            <h3 className="text-sm sm:text-base md:text-lg font-semibold leading-snug mb-4">
              {currentQuestion.question}
            </h3>

            {/* Answer buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {currentQuestion.options.map((option, i) => (
                <button
                  key={i}
                  className="bg-gray-700/80 hover:bg-gray-600 active:scale-95 text-white py-3 px-4 rounded-xl text-sm text-left transition-all duration-150 font-medium leading-snug"
                  style={{ minHeight: 52 }}
                  onClick={() => handleAnswer(option)}
                >
                  <span className="text-white/40 text-xs mr-2">{String.fromCharCode(65 + i)}.</span>
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Combo indicator */}
        {combo >= 2 && (
          <div className="text-center">
            <span className="text-yellow-400 text-xs font-bold tracking-widest uppercase animate-pulse">
              🔥 Combo ×{combo}
            </span>
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Game Over ────────────────────────────────────────────────────
  const renderGameOver = () => {
    const map = {
      libre:           { scores: highScoresLibre,  best: personalBestLibre },
      normal:          { scores: highScoresNormal,  best: personalBestNormal },
      ultrarrelampago: { scores: highScoresUltra,   best: personalBestUltra },
    };
    const { scores: hs, best: pb } = map[mode];

    return (
      <div className="w-full max-w-[95vw] sm:max-w-md text-white">
        <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-5 sm:p-7 text-center">
          <h1 className="text-2xl sm:text-4xl font-extrabold mb-2">¡Juego Terminado!</h1>
          <p className="text-3xl sm:text-4xl font-black text-yellow-400 mb-1">{score}</p>
          <p className="text-white/50 text-sm mb-4">puntos</p>

          {user && pb > 0 && (
            <p className="text-sm text-white/60 mb-4">
              Tu mejor en <strong>{mode}</strong>: <span className="text-purple-400 font-bold">{pb}</span>
            </p>
          )}

          {/* Top 5 */}
          {user && hs.length > 0 && (
            <div className="bg-white/5 rounded-xl p-3 mb-5 text-left">
              <h3 className="text-xs font-semibold text-white/45 uppercase tracking-widest mb-2">Top 5 — {mode}</h3>
              {hs.map((h, i) => (
                <div key={i} className="flex justify-between py-1 border-b border-white/5 text-xs">
                  <span>{i + 1}. <strong>{h.name}</strong></span>
                  <span className="text-white/60">{h.score}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-all"
              onClick={() => { setGameState('menu'); setCurrentQuestionIndex(0); }}
            >
              Volver al Menú
            </button>
            {onClose && (
              <button
                className="bg-white/10 hover:bg-white/20 text-white px-6 py-2.5 rounded-xl text-sm active:scale-95 transition-all"
                onClick={onClose}
              >
                Salir
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Root ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex items-center justify-center w-full h-full min-h-0 overflow-auto p-3 sm:p-5"
      style={{ background: 'linear-gradient(to bottom, #111827, #030712)' }}
    >
      {gameState === 'menu'     && renderMenu()}
      {gameState === 'playing'  && renderGame()}
      {gameState === 'gameOver' && renderGameOver()}
    </div>
  );
};

export default TriviaRelampago;
